// @givmo/board — the signing window's chip, in the director header (derivation §8.3; D-CBG-18).
//
// It reads `/me`'s `signing` (computed server-side, so law 8 holds) and counts down from `closes_at`
// on the reader's clock. It shows ONLY while the window is open, quiet until it matters:
//
//   open        "Signing open · 47 min left"            a plain pill; tap or hover for the detail
//   closing     an amber dot · "Signing closes in 4 min"  under five minutes, the one time it speaks up
//
// A CLOSED window shows nothing. There is no "Confirm to sign" anywhere: pressing Sign brings the
// confirmation up by itself, and the act completes after it. Jonathan, 2026-10-02: "why would we ever
// show confirm to sign button if there is nothing to sign? … when you go to sign, if we need you to
// confirm, we should just automatically bring up the authentication".
//
// At `closes_at` the chip hides and asks `/me` again, so the server's answer replaces the clock's.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover, cn } from '@givmo/design-system';
import { displayClockTime } from '../../lib/display';
import { useMinuteClock } from '../../lib/minute-clock';
import { boardDirectorMeKey, useDirectorMe } from '../../director/director-me';
import type { DirectorSigning } from '../../director/director-wire';

/** Minutes left, rounded UP: "1 min" until the last minute is spent, never "0 min" while open. */
export function minutesLeft(closesAt: string, nowMs: number): number {
  const closes = Date.parse(closesAt);
  if (Number.isNaN(closes)) return 0;
  return Math.max(0, Math.ceil((closes - nowMs) / 60000));
}

/** "your passkey" / "your authenticator app": the method `/me` reports, never a device's word. */
export function signingMethodWords(method: string | null): string {
  return method === 'totp' ? 'your authenticator app' : 'your passkey';
}

/** The window as the chip shows it, or null when it shows nothing. */
export function openWindow(signing: DirectorSigning | undefined, nowMs: number) {
  if (signing?.open !== true || signing.closes_at === null || signing.confirmed_at === null) {
    return null;
  }
  const left = minutesLeft(signing.closes_at, nowMs);
  return left === 0
    ? null
    : { left, closesAt: signing.closes_at, confirmedAt: signing.confirmed_at };
}

export function SigningChip() {
  const meQuery = useDirectorMe();
  const queryClient = useQueryClient();
  const signing = meQuery.data?.signing;
  const now = useMinuteClock(signing?.open === true ? signing.closes_at : null);
  const window_ = openWindow(signing, now);
  const lapsed = signing?.open === true && window_ === null;

  useEffect(() => {
    // The clock says the window has closed: ask the server, whose answer is the one that counts.
    if (lapsed) void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
  }, [lapsed, queryClient]);

  if (window_ === null || signing === undefined) return null;
  const closing = window_.left < 5;

  return (
    <Popover.Root>
      <Popover.Trigger
        openOnHover
        delay={150}
        className={cn(
          'inline-flex h-32 shrink-0 items-center gap-8 whitespace-nowrap rounded-full border bg-surface px-12 font-sans text-s13 font-w600 tracking-givmo outline-none transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
          closing ? 'border-warning-500 text-text-strong' : 'border-border text-text-secondary',
        )}
        data-testid="signing-chip"
      >
        {closing ? (
          <span aria-hidden className="size-8 shrink-0 rounded-full bg-warning-500" />
        ) : null}
        {closing ? (
          <span>Signing closes in {window_.left}&nbsp;min</span>
        ) : (
          <span>
            Signing open<span className="text-text-tertiary"> · </span>
            {window_.left}&nbsp;min left
          </span>
        )}
      </Popover.Trigger>
      <Popover.Content
        saas
        side="bottom"
        align="end"
        sideOffset={8}
        collisionPadding={16}
        className="flex w-max max-w-[340px] flex-col gap-4 px-14 py-12"
        testId="signing-chip-detail"
      >
        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          You confirmed with {signingMethodWords(signing.method)} at{' '}
          {displayClockTime(window_.confirmedAt)}.
        </p>
        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          You can sign until {displayClockTime(window_.closesAt)} on this device.
        </p>
      </Popover.Content>
    </Popover.Root>
  );
}
